import { useState, useCallback } from "react";

/* ─────────────────────────────────────────────────────────────
   Backend-powered Stripe Checkout

   Flow:
     1. Frontend calls checkout(priceKey)
     2. Backend hits Stripe API, creates a Checkout Session
     3. Backend returns { url }
     4. Frontend redirects the browser to session.url

   priceKey  — one of the STRIPE_* env keys WITHOUT the REACT_APP_ prefix,
               e.g. "STRIPE_AI_STARTER", "REACT_APP_STRIPE_DEV_PKG_A"
               (both prefixed and un-prefixed forms are accepted)

   REACT_APP_API_URL must be set in frontend .env:
     REACT_APP_API_URL=http://localhost:4242
     (swap for your live backend URL before deploying)
─────────────────────────────────────────────────────────────── */

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4242";

/**
 * Normalise the env key so both:
 *   "REACT_APP_STRIPE_AI_STARTER"  (what App.js passes via plan.stripeEnv)
 *   "STRIPE_AI_STARTER"            (bare form)
 * resolve to the key the backend expects.
 */
function normalisePriceKey(envKey) {
  return envKey
    ? envKey.replace(/^REACT_APP_/, "")
    : envKey;
}

export function useStripeCheckout() {
  const [loading, setLoading] = useState(false);
  const [error,   setError  ] = useState(null);

  const checkout = useCallback(async (priceKeyOrId) => {
    setError(null);
    setLoading(true);

    try {
      const isDirectId = priceKeyOrId && priceKeyOrId.startsWith("price_");
      const body = isDirectId
        ? { priceId: priceKeyOrId }
        : { priceKey: normalisePriceKey(priceKeyOrId) };

      const res = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method : "POST",
        headers: { "Content-Type": "application/json" },
        body   : JSON.stringify(body),
      });

      if (!res.ok) {
        const { error: msg } = await res.json().catch(() => ({}));
        throw new Error(msg || `Server error ${res.status}`);
      }

      const { url } = await res.json();
      if (!url) throw new Error("No checkout URL returned from server.");

      // Redirect to Stripe-hosted checkout page
      window.location.href = url;
    } catch (err) {
      console.error("[Stripe] Checkout error:", err.message);
      setError(err.message);
      setLoading(false); // only reset on error; on success the page navigates away
    }
  }, []);

  return { checkout, loading, error };
}

